import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  useNavigate,
  useParams
} from 'react-router-dom'
import {
  APIProvider,
  AdvancedMarker,
  InfoWindow,
  Map
} from '@vis.gl/react-google-maps'
import type {
  StayMapOption,
  StayRoomOption
} from '../client'
import {
  StayEvents,
  trackEvent
} from '../analytics/posthog'
import {
  useStayOptionSet
} from '../hooks/useStayOptionSet'
import {
  atPropertyLabel,
  choiceProperties,
  findChoice,
  formatCancelDate,
  formatExactPrice,
  formatStayDates,
  refundPolicy,
  roomsOf,
  stayConfirmPath
} from '../utils/stayOptions'
import type {
  ChoiceSource
} from '../utils/stayOptions'
import {
  ALREADY_PICKED,
  HotelImage,
  MAPS_API_KEY,
  OptionSetStatus,
  PageShell
} from './StayPageParts'

// AdvancedMarker requires a cloud map ID. DEMO_MAP_ID is Google's sample
// placeholder: it renders and supports Advanced Markers, but Google documents
// it as not intended for production and it cannot use cloud-based styling.
// Setting the VITE_GOOGLE_MAPS_MAP_ID repo variable to a real map ID replaces
// it everywhere; until then this page ships with Google's default styling.
// @ts-ignore - Vite env types
const MAPS_MAP_ID = (import.meta.env?.VITE_GOOGLE_MAPS_MAP_ID as string | undefined) || 'DEMO_MAP_ID'

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(price)
  } catch {
    return `${currency} ${Math.round(price)}`
  }
}

function formatDistance(km: number): string {
  // Sub-kilometre distances are the whole point in a dense city — "0.3 km"
  // reads as rounding noise where "300 m" reads as walkable.
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`
}

function distanceLabel(option: StayMapOption): string | null {
  // The anchor is per-option, not per-page: a brief naming several acceptable
  // hubs runs one anchored search per hub, so one shortlist legitimately mixes
  // them. Without the name, "1.3 km away" is a number the user can't act on —
  // away from the airport, the venue, the city centre?
  if (option.distance_km == null) {
    return null
  }
  const distance = formatDistance(option.distance_km)
  return option.distance_anchor
    ? `${distance} from ${option.distance_anchor}`
    : `${distance} away`
}

function priceLabel(option: StayMapOption): string {
  const min = option.price_min ?? option.price
  const max = option.price_max ?? option.price
  return max > min
    ? `${formatPrice(min, option.currency)} – ${formatPrice(max, option.currency)}`
    : formatPrice(min, option.currency)
}

interface RoomMatrixData {
  roomNames: string[]
  boardNames: string[]
  // cells[roomName][boardName] = the rate(s) for that pairing, guest-total
  // cheapest first. Per refund policy we keep the offer that costs least to
  // stay in AND — only when that is a different offer — the one with the lowest
  // RATE, so the refundable/non-refundable choice stays visible and the rate the
  // card headlines as `price_min` always appears here too. At most two rows per
  // refund policy, and the second one always carries a fee line explaining why
  // it sorts below a higher-looking rate.
  cells: Record<string, Record<string, StayRoomOption[]>>
}

// What the guest actually pays: the rate plus any taxes/fees the property
// collects at check-in. Mirrors the backend's `guest_total` (GET-11449) — the
// backend picks the headline room this way, so ranking the expanded matrix on
// the bare rate would contradict the very card the user tapped. Falls back to
// the rate when the fee is unknown, or is quoted in another currency (adding
// those would invent a total out of an exchange rate); both are lower bounds.
function guestTotal(room: StayRoomOption): number {
  const extra = room.excluded_taxes_and_fees
  if (extra === null || extra === undefined) {
    return room.price
  }
  const feeCurrency = room.excluded_taxes_currency
  if (feeCurrency !== null && feeCurrency !== undefined
    && feeCurrency !== room.currency) {
    return room.price
  }
  return room.price + extra
}

// Pivot the flat offer list into a room x board grid so every price is visible
// at a glance. First-seen room/board order is preserved.
export function buildRoomMatrix(rooms: StayRoomOption[]): RoomMatrixData {
  const roomNames: string[] = []
  const boardNames: string[] = []
  const cells: Record<string, Record<string, StayRoomOption[]>> = {}
  const collected: Record<string, Record<string, StayRoomOption[]>> = {}
  for (const room of rooms) {
    const roomName = room.room_name ?? 'Room'
    const boardName = room.board_name ?? 'Room only'
    if (!roomNames.includes(roomName)) {
      roomNames.push(roomName)
    }
    if (!boardNames.includes(boardName)) {
      boardNames.push(boardName)
    }
    const row = collected[roomName] ?? {}
    collected[roomName] = row
    const rates = row[boardName] ?? []
    row[boardName] = rates
    rates.push(room)
  }

  // Per refund policy, keep the offer that costs least to actually stay in AND
  // — when that is a different offer — the one with the lowest RATE. Keeping
  // only the guest-total winner would drop the very rate the card headlines as
  // `price_min` / "Cheapest room", leaving the user a price that appears
  // nowhere in the matrix (GET-11449). Both rows sort by guest total, so the
  // lower-rate one renders below with its `+ X at property` line saying why.
  for (const roomName of roomNames) {
    for (const boardName of boardNames) {
      const group = collected[roomName]?.[boardName]
      if (!group) {
        continue
      }
      const kept: StayRoomOption[] = []
      for (const refundable of [true, false]) {
        const kind = group.filter((rate) => rate.refundable === refundable)
        if (kind.length === 0) {
          continue
        }
        const bestTotal = kind.reduce((a, b) => (guestTotal(b) < guestTotal(a) ? b : a))
        const bestRate = kind.reduce((a, b) => (b.price < a.price ? b : a))
        kept.push(bestTotal)
        if (bestRate.offer_id !== bestTotal.offer_id) {
          kept.push(bestRate)
        }
      }
      kept.sort((a, b) => guestTotal(a) - guestTotal(b))
      const row = cells[roomName] ?? {}
      cells[roomName] = row
      row[boardName] = kept
    }
  }
  return { roomNames, boardNames, cells }
}

// Trim LiteAPI's verbose board labels so they fit the matrix header.
function shortBoard(board: string): string {
  return board.replace(/ included$/i, '').trim()
}

// Compact per-rate cancellation label for a matrix cell, including the date.
function refundLabel(room: StayRoomOption): string {
  if (!room.refundable) {
    return 'non-ref'
  }
  const until = room.free_cancel_until ? formatCancelDate(room.free_cancel_until) : null
  return until ? `✓ free · ${until}` : '✓ free'
}

function googleMapsHref(option: StayMapOption): string {
  // Query by name + address so Google opens the hotel's place card (reviews,
  // photos, info) rather than dropping a pin at bare coordinates.
  const query = [option.name, option.address, option.city]
    .filter(Boolean)
    .join(', ')
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}

function Stars({ stars }: { stars: number | null | undefined }): JSX.Element | null {
  if (stars == null) {
    return null
  }
  const rounded = Math.round(stars)
  return (
    <span className="text-catch-red" aria-label={`${rounded} star`}>
      {'★'.repeat(rounded)}
    </span>
  )
}

function PriceComparison({ option }: { option: StayMapOption }): JSX.Element | null {
  // LiteAPI returns what the SAME offer sells for on a mainstream OTA, source
  // attached (observed: booking.com). It only reaches us when it beats our
  // price, so its presence means there is a real saving.
  //
  // The benchmark describes the CHEAPEST room specifically, but the card
  // headlines a min-max range — so an unqualified "Save 20%" next to
  // "€142 – €189" is false for anyone who books the €189 room, which costs
  // MORE than the €177 struck through. Name the room the comparison is about
  // whenever the card shows a range; a money claim the user can misattribute
  // is worse than no claim.
  const ota = option.suggested_selling_price
  const source = option.suggested_selling_source
  const ours = option.price_min ?? option.price
  if (ota == null || ota <= ours) {
    return null
  }
  const saving = Math.round(((ota - ours) / ota) * 100)
  if (saving < 1) {
    return null
  }
  const isRange = option.price_max != null && option.price_max > ours
  const subject = isRange
    ? `Cheapest room ${formatPrice(ours, option.currency)}`
    : 'This stay'
  return (
    <p className="mt-1 text-[11px] leading-tight text-gray-600">
      {subject} — {source ?? 'elsewhere'} shows{' '}
      <span className="line-through tabular-nums">
        {formatPrice(ota, option.currency)}
      </span>
      , <span className="font-medium text-green-700">{saving}% less here</span>
    </p>
  )
}

function UsdPriceNote({
  option,
  showsUsd
}: {
  option: StayMapOption
  showsUsd: boolean | null | undefined
}): JSX.Element | null {
  // Catch pays the property in its own currency and bills the card in USD, so
  // a £928 stay reaches the statement as ~$1,245 — a number the user would
  // otherwise never see before it was charged.
  //
  // Hedged, unlike the confirmation and the emails: nothing is booked yet, so
  // this figure is today\'s rate rather than the one fixed on the row at
  // booking. Same-day it is exactly what gets charged; across days it can drift
  // a fraction of a percent, and promising it outright would be a promise we
  // have not made yet.
  const usd = option.price_usd
  if (usd == null || !showsUsd) {
    return null
  }
  return (
    <p className="mt-1 text-[11px] leading-tight text-gray-500">
      About {formatPrice(usd, 'USD')} on your card
    </p>
  )
}

function ExcludedTaxesNote({ option }: { option: StayMapOption }): JSX.Element | null {
  // Money the PROPERTY collects at check-in (e.g. the French taxe de sejour, a
  // resort fee) and that is NOT in the total shown above — leaving it
  // undisclosed turns a genuine saving into a surprise at the desk. `0` means
  // "breakdown seen, nothing extra"; `null` means LiteAPI told us nothing, so
  // we stay silent rather than imply there is nothing to pay.
  //
  // Deliberately "taxes and fees", not "taxes": the excluded lines include
  // resort and cleaning fees, and telling someone "tax" then charging a resort
  // fee is a promise we didn't keep.
  const extra = option.excluded_taxes_and_fees
  if (extra == null || extra <= 0) {
    return null
  }
  return (
    <p className="mt-1 text-[11px] text-gray-500">
      + {formatExactPrice(extra, option.excluded_taxes_currency ?? option.currency)}
      {' '}in taxes and fees, paid at the property
    </p>
  )
}

export function StayOptionsPage(): JSX.Element {
  const { token } = useParams<{ token: string }>()
  const { state, data, retry } = useStayOptionSet(token, 'list')
  const alreadyTaken = data?.already_selected ?? false
  const [activeOfferId, setActiveOfferId] = useState<string | null>(null)
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set())
  const navigate = useNavigate()

  const toggleExpanded = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const optionCount = data?.options.length
  useEffect(() => {
    if (optionCount !== undefined) {
      trackEvent(StayEvents.OPTIONS_VIEWED, { token, options: optionCount })
    }
  }, [token, optionCount])

  // A tap only opens the confirmation page; nothing reaches the assistant until
  // the owner confirms there.
  const requestChoice = useCallback(
    (option: StayMapOption, offerId: string, source: ChoiceSource): void => {
      const choice = findChoice([option], offerId)
      if (!token || !choice) {
        return
      }
      trackEvent(StayEvents.OPTION_TAPPED, choiceProperties(choice, source))
      navigate(stayConfirmPath(token, offerId, source))
    },
    [token, navigate]
  )

  const mapCenter = useMemo(() => {
    const located = (data?.options ?? []).filter(
      (o) => o.latitude != null && o.longitude != null
    )
    if (located.length === 0) {
      return null
    }
    const lat = located.reduce((sum, o) => sum + (o.latitude as number), 0) / located.length
    const lng = located.reduce((sum, o) => sum + (o.longitude as number), 0) / located.length
    return { lat, lng }
  }, [data])

  // Depends only on the fetched payload, but the render body re-runs on every
  // marker tap and every room-list toggle. Must sit above the early returns —
  // a hook called after one would break the Rules of Hooks.
  const stayDates = useMemo(
    () => formatStayDates(data?.search_params),
    [data?.search_params]
  )

  if (state !== 'ready' || !data) {
    return <OptionSetStatus state={state === 'ready' ? 'loading' : state} onRetry={retry} />
  }

  return (
    <PageShell>
      <header className="border-b border-catch-beige-dark px-6 py-5">
        <h1 className="font-display text-2xl sm:text-3xl">Places to stay</h1>
        {stayDates && (
          <p className="mt-1 font-sans text-sm font-medium text-catch-black">
            {stayDates}
          </p>
        )}
        {data.search_summary && (
          <p className="mt-1 text-sm text-gray-600">{data.search_summary}</p>
        )}
        {alreadyTaken && (
          <p className="mt-2 text-sm text-catch-red">{ALREADY_PICKED}</p>
        )}
      </header>

      <div className="flex flex-col-reverse lg:h-[calc(100vh-89px)] lg:flex-row">
        <div className="lg:w-[440px] lg:shrink-0 lg:overflow-y-auto">
          <ul className="divide-y divide-catch-beige-dark">
            {data.options.map((option) => (
              <li key={option.offer_id}>
                <OptionCard
                  option={option}
                  showsUsd={data.shows_usd_prices}
                  isActive={activeOfferId === option.offer_id}
                  expanded={expandedKeys.has(option.offer_id)}
                  disabled={alreadyTaken}
                  onHover={() => setActiveOfferId(option.offer_id)}
                  onToggle={() => {
                    if (!expandedKeys.has(option.offer_id)) {
                      trackEvent(StayEvents.LISTING_EXPANDED, { hotel: option.name })
                    }
                    toggleExpanded(option.offer_id)
                  }}
                  onChoose={(offerId) => requestChoice(option, offerId, 'room_table')}
                />
              </li>
            ))}
          </ul>
        </div>

        <div className="h-60 lg:h-auto lg:flex-1">
          <OptionsMap
            options={data.options}
            center={mapCenter}
            activeOfferId={activeOfferId}
            onActivate={setActiveOfferId}
            onChoose={(option) => requestChoice(option, option.offer_id, 'map_popup')}
            choosingDisabled={alreadyTaken}
          />
        </div>
      </div>

      <footer className="px-6 py-4 text-center text-xs text-gray-400">
        Powered by Catch · prices are indicative and confirmed at booking
        {/* "300 m from Gare de Lyon" reads as a walk; it's a straight line, and
            the real walk is longer. Disclosed once here rather than appended to
            every card, matching how the price caveat above is handled. */}
        {data.options.some((o) => o.distance_km != null)
          && ' · distances are straight-line'}
      </footer>
    </PageShell>
  )
}

function OptionCard({
  option,
  showsUsd,
  isActive,
  expanded,
  disabled,
  onHover,
  onToggle,
  onChoose
}: {
  option: StayMapOption
  showsUsd: boolean | null | undefined
  isActive: boolean
  expanded: boolean
  disabled: boolean
  onHover: () => void
  onToggle: () => void
  onChoose: (offerId: string) => void
}): JSX.Element {
  const matrix = buildRoomMatrix(roomsOf(option))

  return (
    <div className={`transition-colors ${isActive ? 'bg-catch-grey' : ''}`}>
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={onHover}
        aria-expanded={expanded}
        className="flex w-full items-start gap-4 p-4 text-left"
      >
        <HotelImage
          option={option}
          width={240}
          wrapperClass="h-24 w-24 shrink-0 rounded-xl"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h2 className="truncate font-display text-base font-medium">{option.name}</h2>
            <div className="shrink-0 text-right">
              <div className="whitespace-nowrap font-sans text-sm font-semibold tabular-nums">
                {priceLabel(option)}
              </div>
              <div className="text-[11px] text-gray-500">
                {option.excluded_taxes_and_fees != null
                  && option.excluded_taxes_and_fees > 0
                  ? 'total stay, before local taxes'
                  : 'total stay'}
              </div>
            </div>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-600">
            <Stars stars={option.stars} />
            {/* `/10` is explicit because the 0-5 star glyphs sit right beside it. */}
            {option.rating != null && (
              <span>
                {option.rating.toFixed(1)}/10
                {option.review_count != null && ` (${option.review_count})`}
              </span>
            )}
            {distanceLabel(option) && (
              <span className="text-gray-700">{distanceLabel(option)}</span>
            )}
            {option.refundable && <span className="text-green-700">Refundable</span>}
          </div>
          <PriceComparison option={option} />
          <ExcludedTaxesNote option={option} />
          <UsdPriceNote option={option} showsUsd={showsUsd} />
          {option.rationale && (
            <p className="mt-1 line-clamp-2 text-sm text-gray-700">{option.rationale}</p>
          )}
          <div className="mt-1.5 text-xs font-medium text-catch-red">
            {expanded
              ? 'Hide rooms ▴'
              : `View ${matrix.roomNames.length} room option${matrix.roomNames.length === 1 ? '' : 's'} ▾`}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 animate-fade-in">
          <HotelImage option={option} width={800} wrapperClass="h-40 w-full rounded-xl" />
          <a
            href={option.google_maps_url ?? googleMapsHref(option)}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent(StayEvents.OPEN_IN_GOOGLE_MAPS, { hotel: option.name })}
            className="mt-2 inline-block text-sm text-gray-500 underline hover:text-catch-black"
          >
            Open in Google Maps
          </a>
          <RoomMatrix
            matrix={matrix}
            disabled={disabled}
            onChoose={onChoose}
          />
        </div>
      )}
    </div>
  )
}

function RoomMatrix({
  matrix,
  disabled,
  onChoose
}: {
  matrix: RoomMatrixData
  disabled: boolean
  onChoose: (offerId: string) => void
}): JSX.Element {
  const { roomNames, boardNames, cells } = matrix
  return (
    <div className="mt-3">
      <div className="overflow-x-auto rounded-xl border border-catch-beige-dark">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-catch-grey">
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Room</th>
              {boardNames.map((board) => (
                <th
                  key={board}
                  className="whitespace-nowrap px-1.5 py-1.5 text-center font-medium text-gray-500"
                >
                  {shortBoard(board)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roomNames.map((roomName) => (
              <tr key={roomName} className="border-t border-catch-beige-dark/70">
                <td className="px-2 py-1.5 align-middle font-medium">{roomName}</td>
                {boardNames.map((board) => {
                  const rates = cells[roomName]?.[board]
                  return (
                    <td key={board} className="px-1 py-1 align-middle">
                      {rates && rates.length > 0
                        ? (
                          <div className="space-y-1">
                            {rates.map((rate) => {
                              const atProperty = atPropertyLabel(rate)
                              return (
                              <button
                                key={rate.offer_id}
                                type="button"
                                onClick={() => onChoose(rate.offer_id)}
                                disabled={disabled}
                                title={refundPolicy(rate)}
                                className="block w-full whitespace-nowrap rounded-md border border-catch-beige-dark px-1.5 py-1 transition-colors hover:border-catch-red disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <span className="block text-sm font-semibold tabular-nums">
                                  {formatPrice(rate.price, rate.currency)}
                                </span>
                                {atProperty !== null && (
                                  <span className="block text-[10px] leading-tight text-gray-500 tabular-nums">
                                    {atProperty}
                                  </span>
                                )}
                                <span
                                  className={`block text-[10px] leading-tight ${
                                    rate.refundable ? 'text-green-700' : 'text-gray-400'
                                  }`}
                                >
                                  {refundLabel(rate)}
                                </span>
                              </button>
                              )
                            })}
                          </div>
                          )
                        : (
                          <span className="block text-center text-gray-300">—</span>
                          )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-1.5 text-[11px] text-gray-400">
        Tap a price to choose that room &amp; board.
      </p>
    </div>
  )
}

function OptionsMap({
  options,
  center,
  activeOfferId,
  onActivate,
  onChoose,
  choosingDisabled
}: {
  options: StayMapOption[]
  center: { lat: number, lng: number } | null
  activeOfferId: string | null
  onActivate: (offerId: string | null) => void
  onChoose: (option: StayMapOption) => void
  choosingDisabled: boolean
}): JSX.Element {
  const located = options.filter((o) => o.latitude != null && o.longitude != null)
  const active = located.find((o) => o.offer_id === activeOfferId) ?? null

  // Frame the map to all pins when there's more than one; otherwise centre on
  // the single option. Auto-fit beats a guessed zoom across cities/spreads.
  const bounds = located.length > 1
    ? located.reduce(
      (b, o) => ({
        north: Math.max(b.north, o.latitude as number),
        south: Math.min(b.south, o.latitude as number),
        east: Math.max(b.east, o.longitude as number),
        west: Math.min(b.west, o.longitude as number)
      }),
      { north: -90, south: 90, east: -180, west: 180 }
    )
    : null

  if (!MAPS_API_KEY) {
    return (
      <div className="flex h-full items-center justify-center bg-catch-beige px-6 text-center text-sm text-gray-500">
        Map preview is unavailable here. Use “Open in Google Maps” on any option
        to see where it is.
      </div>
    )
  }

  if (!center) {
    return (
      <div className="flex h-full items-center justify-center bg-catch-beige px-6 text-center text-sm text-gray-500">
        We don&apos;t have map locations for these options.
      </div>
    )
  }

  return (
    <APIProvider apiKey={MAPS_API_KEY}>
      <Map
        {...(bounds
          ? { defaultBounds: { ...bounds, padding: 64 } }
          : { defaultCenter: center, defaultZoom: 14 })}
        mapId={MAPS_MAP_ID}
        gestureHandling="greedy"
        disableDefaultUI
        className="h-full w-full"
      >
        {located.map((option) => (
          <AdvancedMarker
            key={option.offer_id}
            position={{ lat: option.latitude as number, lng: option.longitude as number }}
            onClick={() => {
              trackEvent(StayEvents.MAP_PIN_TAPPED, { hotel: option.name })
              onActivate(option.offer_id)
            }}
          >
            <div
              className={`rounded-full border px-2 py-1 text-xs font-semibold shadow-soft ${
                activeOfferId === option.offer_id
                  ? 'border-catch-red bg-catch-red text-catch-white'
                  : 'border-catch-beige-dark bg-catch-white text-catch-black'
              }`}
            >
              {formatPrice(option.price_min ?? option.price, option.currency)}
            </div>
          </AdvancedMarker>
        ))}

        {active && active.latitude != null && active.longitude != null && (
          <InfoWindow
            position={{ lat: active.latitude, lng: active.longitude }}
            onCloseClick={() => onActivate(null)}
            pixelOffset={[0, -36]}
          >
            <div className="max-w-[220px] font-sans">
              <div className="font-medium">{active.name}</div>
              <div className="mt-0.5 text-sm text-gray-600">
                {priceLabel(active)} total
              </div>
              {distanceLabel(active) && (
                <div className="text-xs text-gray-600">{distanceLabel(active)}</div>
              )}
              <ExcludedTaxesNote option={active} />
              <button
                type="button"
                onClick={() => onChoose(active)}
                disabled={choosingDisabled}
                className="mt-2 rounded-full bg-catch-red px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
              >
                Choose this
              </button>
            </div>
          </InfoWindow>
        )}
      </Map>
    </APIProvider>
  )
}
